import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Shield,
  ShieldCheck,
  Plus,
  Search,
  Edit2,
  Trash2,
  CheckCircle,
  XCircle,
  Building2,
  UserCheck,
  AlertCircle,
  Calendar,
  Layers,
  RefreshCw,
  X,
  ChevronDown,
  Info
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const BookingControllerRules = () => {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Options loaded from backend
  const [departments, setDepartments] = useState([]);
  const [controllers, setControllers] = useState([]);
  const [optionsLoading, setOptionsLoading] = useState(false);

  // Modal State
  const [showModal, setShowModal] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [saving, setSaving] = useState(false);

  // Form State
  const [form, setForm] = useState({
    rule_name: '',
    department_ids: [],
    department_names: [],
    controller_employee_id: '',
    booking_type: 'all',
    effective_from_date: '',
    effective_to_date: '',
    priority: 10,
    is_active: true
  });

  // Department search inside multi-select dropdown
  const [deptSearch, setDeptSearch] = useState('');
  const [deptDropdownOpen, setDeptDropdownOpen] = useState(false);

  // Controller search
  const [controllerSearch, setControllerSearch] = useState('');

  useEffect(() => {
    fetchRules();
    fetchOptions();
  }, []);

  const fetchRules = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API_URL}/travel-desk/booking-controller-rules`, { withCredentials: true });
      setRules(data.rules || []);
    } catch (err) {
      toast.error('Failed to load Booking Controller rules.');
    } finally {
      setLoading(false);
    }
  };

  const fetchOptions = async () => {
    try {
      setOptionsLoading(true);
      const [deptRes, ctrlRes] = await Promise.all([
        axios.get(`${API_URL}/travel-desk/booking-controller-rules/department-options`, { withCredentials: true }),
        axios.get(`${API_URL}/travel-desk/booking-controller-rules/eligible-controllers`, { withCredentials: true })
      ]);
      setDepartments(deptRes.data.departments || []);
      setControllers(ctrlRes.data.controllers || []);
    } catch (err) {
      toast.error('Failed to load departments or controllers options.');
    } finally {
      setOptionsLoading(false);
    }
  };

  const handleOpenCreateModal = () => {
    setEditingRule(null);
    setForm({
      rule_name: '',
      department_ids: [],
      department_names: [],
      controller_employee_id: '',
      booking_type: 'all',
      effective_from_date: new Date().toISOString().split('T')[0],
      effective_to_date: '',
      priority: 10,
      is_active: true
    });
    setDeptSearch('');
    setControllerSearch('');
    setShowModal(true);
  };

  const handleOpenEditModal = (rule) => {
    setEditingRule(rule);
    setForm({
      rule_name: rule.rule_name || '',
      department_ids: rule.department_ids || [],
      department_names: rule.department_names || [],
      controller_employee_id: rule.controller_employee_id || '',
      booking_type: rule.booking_type || 'all',
      effective_from_date: rule.effective_from_date || '',
      effective_to_date: rule.effective_to_date || '',
      priority: rule.priority ?? 10,
      is_active: rule.is_active ?? true
    });
    setDeptSearch('');
    setControllerSearch('');
    setShowModal(true);
  };

  const handleToggleDept = (dept) => {
    const isSelected = form.department_ids.includes(dept.id);
    let newIds, newNames;
    if (isSelected) {
      newIds = form.department_ids.filter((id) => id !== dept.id);
      newNames = form.department_names.filter((n) => n !== dept.name);
    } else {
      newIds = [...form.department_ids, dept.id];
      newNames = [...form.department_names, dept.name];
    }
    setForm({ ...form, department_ids: newIds, department_names: newNames });
  };

  const handleRemoveDept = (deptId) => {
    const idx = form.department_ids.indexOf(deptId);
    if (idx !== -1) {
      const newIds = [...form.department_ids];
      const newNames = [...form.department_names];
      newIds.splice(idx, 1);
      newNames.splice(idx, 1);
      setForm({ ...form, department_ids: newIds, department_names: newNames });
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.rule_name.trim()) {
      toast.error('Rule Name is required.');
      return;
    }
    if (form.department_ids.length === 0) {
      toast.error('Please select at least one department.');
      return;
    }
    if (!form.controller_employee_id) {
      toast.error('Please assign a Booking Controller employee.');
      return;
    }

    try {
      setSaving(true);
      if (editingRule) {
        await axios.put(
          `${API_URL}/travel-desk/booking-controller-rules/${editingRule._id}`,
          form,
          { withCredentials: true }
        );
        toast.success('Booking Controller Rule updated successfully!');
      } else {
        await axios.post(
          `${API_URL}/travel-desk/booking-controller-rules`,
          form,
          { withCredentials: true }
        );
        toast.success('Booking Controller Rule created successfully!');
      }
      setShowModal(false);
      fetchRules();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save rule.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRule = async (ruleId, ruleName) => {
    if (!window.confirm(`Are you sure you want to deactivate rule "${ruleName}"?`)) return;
    try {
      await axios.delete(`${API_URL}/travel-desk/booking-controller-rules/${ruleId}`, { withCredentials: true });
      toast.success(`Rule "${ruleName}" deactivated.`);
      fetchRules();
    } catch (err) {
      toast.error('Failed to deactivate rule.');
    }
  };

  const filteredRules = rules.filter((r) => {
    const q = searchQuery.toLowerCase();
    const nameMatch = r.rule_name?.toLowerCase().includes(q);
    const ctrlMatch = r.controller_name?.toLowerCase().includes(q) || r.controller_code?.toLowerCase().includes(q);
    const deptMatch = r.department_names?.some((d) => d.toLowerCase().includes(q));
    return nameMatch || ctrlMatch || deptMatch;
  });

  const filteredDepartments = departments.filter((d) =>
    d.name.toLowerCase().includes(deptSearch.toLowerCase()) ||
    d.code.toLowerCase().includes(deptSearch.toLowerCase())
  );

  const filteredControllers = controllers.filter((c) =>
    c.name.toLowerCase().includes(controllerSearch.toLowerCase()) ||
    c.employee_code.toLowerCase().includes(controllerSearch.toLowerCase()) ||
    c.department.toLowerCase().includes(controllerSearch.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Top Banner Card */}
      <div className="bg-gradient-to-r from-slate-900 via-indigo-900 to-blue-900 text-white rounded-2xl p-6 shadow-xl relative overflow-hidden">
        <div className="absolute right-0 top-0 translate-x-1/4 -translate-y-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="space-y-1.5">
            <div className="flex items-center gap-2.5">
              <div className="p-2.5 bg-amber-400/20 text-amber-300 rounded-xl border border-amber-400/30">
                <ShieldCheck className="w-6 h-6" />
              </div>
              <h2 className="text-xl font-black tracking-tight text-white">Booking Controller Configuration</h2>
            </div>
            <p className="text-xs text-blue-200 max-w-2xl leading-relaxed">
              Define department-wise assignment rules for outstation travel bookings. All approved travel requests from mapped departments are automatically routed to their assigned Booking Controller's queue.
            </p>
          </div>

          <div className="flex items-center gap-3 shrink-0">
            <button
              onClick={fetchRules}
              className="p-2.5 bg-white/10 hover:bg-white/20 text-white rounded-xl transition-all border border-white/10 flex items-center gap-1.5 text-xs font-bold"
              title="Refresh Rules"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </button>

            <button
              onClick={handleOpenCreateModal}
              className="px-4 py-2.5 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-slate-950 font-black text-xs rounded-xl shadow-lg flex items-center gap-2 transition-all transform active:scale-95"
            >
              <Plus className="w-4 h-4" /> Create Controller Rule
            </button>
          </div>
        </div>
      </div>

      {/* Control Toolbar */}
      <div className="bg-white rounded-2xl p-4 border border-slate-200 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3.5 top-3 text-slate-400" />
          <input
            type="text"
            placeholder="Search rules, department, or controller name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 text-xs bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 font-medium"
          />
        </div>

        <div className="flex items-center gap-3 text-xs font-semibold text-slate-500">
          <span>Total Rules: <strong className="text-slate-800">{rules.length}</strong></span>
          <span>•</span>
          <span>Active Rules: <strong className="text-emerald-600">{rules.filter((r) => r.is_active).length}</strong></span>
        </div>
      </div>

      {/* Rules Grid / List */}
      {loading ? (
        <div className="bg-white rounded-2xl p-12 text-center border border-slate-200">
          <RefreshCw className="w-8 h-8 text-indigo-600 animate-spin mx-auto mb-3" />
          <p className="text-xs text-slate-500 font-semibold">Loading Booking Controller Rules...</p>
        </div>
      ) : filteredRules.length === 0 ? (
        <div className="bg-white rounded-2xl p-12 text-center border border-slate-200">
          <Shield className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <h3 className="text-sm font-bold text-slate-700">No Booking Controller Rules Found</h3>
          <p className="text-xs text-slate-400 mt-1 max-w-md mx-auto">
            {searchQuery ? 'No rules match your search query.' : 'Create department assignment rules to assign specific Booking Controllers for approved travel requests.'}
          </p>
          {!searchQuery && (
            <button
              onClick={handleOpenCreateModal}
              className="mt-4 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs rounded-xl shadow-md inline-flex items-center gap-2"
            >
              <Plus className="w-4 h-4" /> Create First Rule
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {filteredRules.map((rule) => (
            <div
              key={rule._id}
              className={`bg-white rounded-2xl border transition-all duration-200 hover:shadow-lg flex flex-col justify-between ${
                rule.is_active ? 'border-slate-200 hover:border-indigo-300' : 'border-slate-200 bg-slate-50/70 opacity-75'
              }`}
            >
              <div className="p-5 space-y-4">
                {/* Header */}
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-extrabold text-sm text-slate-900">{rule.rule_name}</h3>
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider ${
                        rule.is_active ? 'bg-emerald-100 text-emerald-800 border border-emerald-200' : 'bg-slate-200 text-slate-600'
                      }`}>
                        {rule.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    <p className="text-[11px] text-slate-400 font-mono mt-0.5">Priority Order: #{rule.priority ?? 10}</p>
                  </div>

                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => handleOpenEditModal(rule)}
                      className="p-1.5 hover:bg-slate-100 text-slate-600 rounded-lg transition-colors"
                      title="Edit Rule"
                    >
                      <Edit2 className="w-3.5 h-3.5" />
                    </button>
                    {rule.is_active && (
                      <button
                        onClick={() => handleDeleteRule(rule._id, rule.rule_name)}
                        className="p-1.5 hover:bg-red-50 text-red-500 rounded-lg transition-colors"
                        title="Deactivate Rule"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>

                {/* Assigned Controller Info */}
                <div className="bg-slate-50 rounded-xl p-3 border border-slate-100 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-indigo-600 to-blue-600 text-white font-black text-sm flex items-center justify-center shrink-0 shadow-sm">
                    {rule.controller_name ? rule.controller_name.charAt(0).toUpperCase() : 'C'}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-indigo-600">Assigned Controller</div>
                    <div className="font-bold text-xs text-slate-900 truncate">{rule.controller_name || 'Unassigned'}</div>
                    <div className="text-[11px] text-slate-500 truncate">
                      {rule.controller_code ? `[${rule.controller_code}] ` : ''}{rule.controller_designation || 'Controller'}
                    </div>
                  </div>
                </div>

                {/* Departments Tag Cloud */}
                <div>
                  <div className="flex items-center gap-1.5 text-[11px] font-bold text-slate-500 mb-2">
                    <Building2 className="w-3.5 h-3.5 text-slate-400" />
                    <span>Mapped Departments ({rule.department_names?.length || rule.department_ids?.length || 0}):</span>
                  </div>

                  <div className="flex flex-wrap gap-1.5 max-h-28 overflow-y-auto pr-1">
                    {rule.department_names && rule.department_names.length > 0 ? (
                      rule.department_names.map((deptName, idx) => (
                        <span
                          key={idx}
                          className="px-2 py-1 bg-indigo-50 border border-indigo-100 text-indigo-700 font-semibold text-[11px] rounded-lg inline-flex items-center gap-1"
                        >
                          <span className="w-1.5 h-1.5 rounded-full bg-indigo-500" />
                          {deptName}
                        </span>
                      ))
                    ) : (
                      <span className="text-xs text-slate-400 italic">No department names mapped</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Footer Metadata */}
              <div className="bg-slate-50/80 px-5 py-2.5 border-t border-slate-100 text-[11px] text-slate-400 flex items-center justify-between">
                <span>Created by {rule.created_by_name || 'Admin'}</span>
                <span>{rule.created_at ? new Date(rule.created_at).toLocaleDateString() : ''}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* CREATE / EDIT MODAL */}
      {showModal && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-3xl max-w-2xl w-full shadow-2xl border border-slate-200 overflow-hidden my-8 animate-in fade-in zoom-in duration-200">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-slate-900 via-indigo-950 to-blue-950 p-6 text-white flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-amber-400/20 text-amber-300 rounded-xl border border-amber-400/30">
                  <ShieldCheck className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-extrabold text-base">{editingRule ? 'Edit Booking Controller Rule' : 'Create Booking Controller Rule'}</h3>
                  <p className="text-xs text-blue-200">Map multiple departments to a specific Booking Controller</p>
                </div>
              </div>

              <button
                onClick={() => setShowModal(false)}
                className="p-2 text-slate-400 hover:text-white rounded-xl hover:bg-white/10 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <form onSubmit={handleSave} className="p-6 space-y-5 max-h-[75vh] overflow-y-auto">
              {/* Rule Name & Priority */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="md:col-span-2 space-y-1">
                  <label className="text-xs font-bold text-slate-700">Rule Name *</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Rule 1 - Operations & Finance"
                    value={form.rule_name}
                    onChange={(e) => setForm({ ...form, rule_name: e.target.value })}
                    className="w-full px-3.5 py-2 text-xs bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 font-semibold"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-700">Evaluation Priority *</label>
                  <input
                    type="number"
                    min="1"
                    max="99"
                    value={form.priority}
                    onChange={(e) => setForm({ ...form, priority: parseInt(e.target.value) || 10 })}
                    className="w-full px-3.5 py-2 text-xs bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 font-mono font-bold"
                  />
                  <span className="text-[10px] text-slate-400">Lower number = higher priority</span>
                </div>
              </div>

              {/* Department Multi-Select */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
                    <Building2 className="w-3.5 h-3.5 text-indigo-600" />
                    Select Department(s) *
                  </label>
                  <span className="text-[11px] font-semibold text-indigo-600">
                    {form.department_ids.length} Selected
                  </span>
                </div>

                {/* Selected Badges */}
                {form.department_ids.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 p-3 bg-indigo-50/60 border border-indigo-100 rounded-xl max-h-32 overflow-y-auto">
                    {form.department_ids.map((dId, idx) => {
                      const name = form.department_names[idx] || dId;
                      return (
                        <span
                          key={dId}
                          className="px-2.5 py-1 bg-white border border-indigo-200 text-indigo-900 font-bold text-[11px] rounded-lg shadow-sm flex items-center gap-1.5"
                        >
                          {name}
                          <button
                            type="button"
                            onClick={() => handleRemoveDept(dId)}
                            className="text-slate-400 hover:text-red-500 transition-colors"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      );
                    })}
                  </div>
                )}

                {/* Searchable Dept Dropdown Container */}
                <div className="border border-slate-200 rounded-xl overflow-hidden bg-slate-50">
                  <div className="p-2 border-b border-slate-200 bg-white flex items-center gap-2">
                    <Search className="w-3.5 h-3.5 text-slate-400 ml-1" />
                    <input
                      type="text"
                      placeholder="Search Department Master..."
                      value={deptSearch}
                      onChange={(e) => setDeptSearch(e.target.value)}
                      className="w-full text-xs outline-none bg-transparent font-medium"
                    />
                  </div>

                  <div className="max-h-40 overflow-y-auto p-2 space-y-1">
                    {optionsLoading ? (
                      <div className="text-center py-4 text-xs text-slate-400">Loading department master...</div>
                    ) : filteredDepartments.length === 0 ? (
                      <div className="text-center py-4 text-xs text-slate-400">No matching departments found.</div>
                    ) : (
                      filteredDepartments.map((dept) => {
                        const isChecked = form.department_ids.includes(dept.id);
                        return (
                          <label
                            key={dept.id}
                            className={`flex items-center justify-between p-2 rounded-lg text-xs font-semibold cursor-pointer transition-colors ${
                              isChecked ? 'bg-indigo-100/70 text-indigo-900' : 'hover:bg-slate-200/60 text-slate-700'
                            }`}
                          >
                            <div className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={() => handleToggleDept(dept)}
                                className="rounded text-indigo-600 focus:ring-indigo-500 w-3.5 h-3.5"
                              />
                              <span>{dept.name}</span>
                            </div>
                            <span className="text-[10px] font-mono text-slate-400 uppercase">Code: {dept.code}</span>
                          </label>
                        );
                      })
                    )}
                  </div>
                </div>
              </div>

              {/* Assigned Booking Controller Selector */}
              <div className="space-y-2">
                <label className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
                  <UserCheck className="w-3.5 h-3.5 text-indigo-600" />
                  Assigned Booking Controller Employee *
                </label>

                <div className="border border-slate-200 rounded-xl overflow-hidden bg-slate-50">
                  <div className="p-2 border-b border-slate-200 bg-white flex items-center gap-2">
                    <Search className="w-3.5 h-3.5 text-slate-400 ml-1" />
                    <input
                      type="text"
                      placeholder="Search active employee name, code, or department..."
                      value={controllerSearch}
                      onChange={(e) => setControllerSearch(e.target.value)}
                      className="w-full text-xs outline-none bg-transparent font-medium"
                    />
                  </div>

                  <div className="max-h-44 overflow-y-auto p-2 space-y-1">
                    {optionsLoading ? (
                      <div className="text-center py-4 text-xs text-slate-400">Loading eligible controllers...</div>
                    ) : filteredControllers.length === 0 ? (
                      <div className="text-center py-4 text-xs text-slate-400">No active employees found.</div>
                    ) : (
                      filteredControllers.map((ctrl) => {
                        const isSelected = form.controller_employee_id === ctrl.employee_id;
                        return (
                          <div
                            key={ctrl.employee_id}
                            onClick={() => setForm({ ...form, controller_employee_id: ctrl.employee_id })}
                            className={`p-2.5 rounded-xl border text-xs cursor-pointer transition-all flex items-center justify-between ${
                              isSelected
                                ? 'bg-indigo-600 text-white border-indigo-600 shadow-sm'
                                : 'bg-white border-slate-200 hover:border-indigo-300 text-slate-800'
                            }`}
                          >
                            <div className="space-y-0.5">
                              <div className="font-bold">{ctrl.name}</div>
                              <div className={`text-[11px] ${isSelected ? 'text-indigo-100' : 'text-slate-500'}`}>
                                {ctrl.employee_code ? `[${ctrl.employee_code}] ` : ''}{ctrl.designation} • Dept: {ctrl.department}
                              </div>
                            </div>

                            {isSelected && <CheckCircle className="w-4 h-4 text-amber-300 shrink-0" />}
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              </div>

              {/* Status and Action Buttons */}
              <div className="pt-2 border-t border-slate-200 flex items-center justify-between">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.is_active}
                    onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                    className="rounded text-indigo-600 focus:ring-indigo-500 w-4 h-4"
                  />
                  <span className="text-xs font-bold text-slate-700">Rule Active</span>
                </label>

                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold rounded-xl transition-colors"
                  >
                    Cancel
                  </button>

                  <button
                    type="submit"
                    disabled={saving}
                    className="px-5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold rounded-xl shadow-md transition-all flex items-center gap-2 disabled:opacity-50"
                  >
                    {saving && <RefreshCw className="w-3.5 h-3.5 animate-spin" />}
                    {editingRule ? 'Update Rule' : 'Save & Active Rule'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookingControllerRules;
